import { BEIGE_500, BEIGE_700, GRAY_600, GRAY_900, GREEN_600 } from 'constants/styling/theme'

import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import Chip from '@mui/material/Chip'
import { FC } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

/**
 * Welcome state shown in the chat popover when the user has no conversations yet.
 * Mirrors the Figma "Placeholder" frame: centered cream avatar + serif display title +
 * description + feature chips with green check icons. The "+ Start new conversation" CTA
 * is rendered by the parent (`NewConversation`) so the button stays pinned at the bottom.
 */
export const ConversationPlaceholder: FC = () => {
  const { t } = useTranslation(['chat'])

  const features = [
    t('chat:placeholder.features.instant_response'),
    t('chat:placeholder.features.smart_guidance'),
    t('chat:placeholder.features.human_escalation'),
  ]

  return (
    <Stack
      flex={1}
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      gap="2rem"
      padding="2.4rem 1.6rem"
    >

      <Avatar
        sx={{
          width: '6rem',
          height: '6rem',
          backgroundColor: BEIGE_500,
          color: GRAY_900,
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: '2.6rem' }} />
      </Avatar>

      <Stack gap="1rem" alignItems="center">
        <Typography variant="display-sm" fontWeight={400} color={GRAY_900}>
          {t('chat:placeholder.title')}
        </Typography>
        <Typography
          variant="text-md"
          color={GRAY_600}
          sx={{ maxWidth: '30rem', lineHeight: 1.45 }}
        >
          {t('chat:placeholder.description')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        flexWrap="wrap"
        gap="0.8rem"
        justifyContent="center"
        sx={{ maxWidth: '32rem' }}
      >
        {features.map((label) => (
          <Chip
            key={label}
            icon={<CheckCircleOutlineIcon sx={{ color: `${GREEN_600} !important`, fontSize: '1.8rem !important' }} />}
            label={label}
            sx={{
              backgroundColor: BEIGE_500,
              color: GRAY_900,
              border: 'none',
              fontSize: '1.3rem',
              fontWeight: 500,
              height: '3.2rem',
              borderRadius: '1.6rem',
              paddingX: '0.4rem',
              // MUI gives the chip icon a negative right margin (glues it to the label) — add a gap.
              '& .MuiChip-icon': { marginLeft: '0.6rem', marginRight: 0 },
              '& .MuiChip-label': { paddingLeft: '0.6rem', paddingRight: '1rem' },
            }}
          />
        ))}
      </Stack>

    </Stack>
  )
}
